import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";

import { AppealChat } from "@/components/AppealChat";
import { DocumentRail } from "@/components/DocumentRail";
import { FieldsPanel } from "@/components/FieldsPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { FieldRow } from "@/api/types";
import { useGetSession } from "@/hooks/api";
import { appealTypesLabel } from "@/lib/document-kinds";
import { useAppStore } from "@/store/app";

type SideTab = "documents" | "fields" | "generate";

export function SidePanel() {
  const tab = useAppStore((s) => s.sideTab);
  const setTab = useAppStore((s) => s.setSideTab);
  const agentStatus = useAppStore((s) => s.agentStatus);
  const view = useAppStore((s) => s.view);
  const sessionId = useAppStore((s) => s.sessionId);
  const letterHtml = useAppStore((s) => s.letterHtml);
  const setView = useAppStore((s) => s.setView);
  const pendingCount = useAppStore((s) => s.pendingEntries.length);

  const { data: session } = useGetSession(sessionId);
  const syntheticLocked = session?.synthetic_locked ?? false;
  const appealTypeLabel = appealTypesLabel(session?.appeal_types ?? null);

  // Sample-data and individual upload/synth flows push tokens into
  // pendingEntries while the documents are still being generated. Block
  // entry into the Appeal Letter tab until the doc set is stable —
  // otherwise the user can land on the AgentPanel and click "Generate
  // letter" against a half-populated session.
  const synthInProgress = pendingCount > 0;

  // Pull the user over to Appeal Letter when the agent transitions to
  // an attention-needed state, so a long-running poll or a document
  // request doesn't sit unseen behind the Documents tab.
  useEffect(() => {
    if (agentStatus === "running" || agentStatus === "needs_document") {
      setTab("generate");
    }
  }, [agentStatus]);

  // When the user opens the letter, surface the refinement chat
  // immediately by switching to the Appeal Letter tab.
  useEffect(() => {
    if (view === "letter") setTab("generate");
  }, [view]);

  // Tab clicks have a side effect on the main panel: clicking
  // "Appeal Letter" while a draft exists should also bring the letter
  // preview back to the left. Documents tab just changes tabs — the
  // rail decides per-row whether to flip view back to analysis.
  function onTabChange(next: SideTab) {
    setTab(next);
    if (next === "generate" && letterHtml) {
      setView("letter");
    }
  }

  return (
    <aside className="border-l bg-white flex flex-col h-full min-h-0">
      {syntheticLocked && appealTypeLabel && (
        <div className="shrink-0 m-3 mb-0 rounded-md border border-violet-200 bg-violet-50/60 px-3 py-2.5">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-violet-700 font-semibold">
            <Sparkles className="w-3.5 h-3.5" />
            Synthetic mode
          </div>
          <div className="mt-0.5 text-xs text-violet-900 font-medium">
            {appealTypeLabel}
          </div>
          <div className="mt-1 text-[11px] text-violet-700/70 leading-snug">
            Real uploads disabled. Start a new session to switch back.
          </div>
        </div>
      )}

      <Tabs
        value={tab}
        onValueChange={(v) => onTabChange(v as SideTab)}
        className="flex flex-col h-full min-h-0"
      >
        <TabsList className="px-3 shrink-0 justify-start">
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="fields">Fields</TabsTrigger>
          <TabsTrigger
            value="generate"
            disabled={synthInProgress}
            title={
              synthInProgress
                ? "Waiting for documents to finish generating…"
                : undefined
            }
            className="disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Appeal Letter
          </TabsTrigger>
        </TabsList>
        <TabsContent
          value="documents"
          forceMount
          className="flex-1 mt-0 min-h-0 data-[state=inactive]:hidden"
        >
          <DocumentRail />
        </TabsContent>
        <TabsContent
          value="fields"
          forceMount
          className="flex-1 mt-0 min-h-0 data-[state=inactive]:hidden overflow-hidden"
        >
          <SessionFields active={tab === "fields"} />
        </TabsContent>
        <TabsContent
          value="generate"
          forceMount
          className="flex-1 mt-0 min-h-0 data-[state=inactive]:hidden"
        >
          <AppealChat />
        </TabsContent>
      </Tabs>
    </aside>
  );
}

function SessionFields({ active }: { active: boolean }) {
  const sessionId = useAppStore((s) => s.sessionId);
  const { data: session } = useGetSession(sessionId);
  const docCount = session?.documents?.length ?? 0;
  const [rows, setRows] = useState<FieldRow[]>([]);

  // Re-fetch when the user opens the tab, or when the doc list grows
  // (extraction runs on each upload/synth, so case content may have
  // changed). Cheap GET — no debouncing needed for the demo.
  useEffect(() => {
    if (!sessionId || !active) return;
    let cancel = false;
    fetch(`/api/sessions/${sessionId}/fields`)
      .then((r) => r.json())
      .then((body) => !cancel && setRows(body?.data ?? []))
      .catch(() => !cancel && setRows([]));
    return () => {
      cancel = true;
    };
  }, [sessionId, active, docCount]);

  if (!sessionId) {
    return (
      <div className="px-4 py-6 text-sm text-muted-foreground">
        No session yet.
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="px-4 py-6 text-sm text-muted-foreground">
        No fields yet — upload or generate a document to populate the case.
      </div>
    );
  }
  return (
    <div className="h-full overflow-y-auto px-3 pt-3 pb-4">
      <FieldsPanel fields={rows} />
    </div>
  );
}
